<template>
  <div class="content-item" :class="classes" @click.stop.prevent="clicked">
    <div class="bar" :style="barStyle" />
    <flex-row gap vcenter>
      <flex-cell shrink v-if="hasAvatar">
        <div class="image-wrapper" :class="basicType">
          <avatar :id="model" :initials="model" />
        </div>
      </flex-cell>
      <flex-cell shrink v-else-if="hasImage">
        <div class="image-wrapper" :class="basicType">
          <ux-image :item="model" :width="100" :height="100" />
        </div>
      </flex-cell>
      <flex-cell shrink v-else-if="hasIcon">
        <div class="icon-wrapper" :class="iconClass">
          <ux-icon :icon="icon" />
        </div>
      </flex-cell>
      <flex-cell class="text" vcenter>
        <flex-cell>
          <slot>
            <div class="item-title">{{ title }}</div>
            <div class="item-summary">{{ summary }}</div>
          </slot>
        </flex-cell>
      </flex-cell>
      <flex-cell shrink v-if="status">
        <div class="pill" :class="status">{{ status }}</div>
      </flex-cell>
      <flex-cell shrink>
        <slot name="actions"></slot>
      </flex-cell>
    </flex-row>
  </div>
</template>
<script>
export default {
  props: {
    manual: {
      type: Boolean,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      model: this.item,
    };
  },
  watch: {
    item(m) {
      this.model = m;
    },
  },
  computed: {
    barStyle() {
      let firstScopeWithColor;

      if (
        this.model.color ||
        this.model.bgColor ||
        this.model.meta?.type === "scope"
      ) {
        firstScopeWithColor = this.model;
      } else {
        firstScopeWithColor = (this.model.meta?.scopes || []).find(function ({
          color,
        }) {
          return !!color;
        });
      }
      if (!firstScopeWithColor) {
        return { display: "none" };
      }

      return {
        background: firstScopeWithColor.bgColor || firstScopeWithColor.color,
      };
    },
    status() {
      if (this.model.meta?.deleted) {
        return "deleted";
      }

      if (this.model.meta?.status === "inactive") {
        return "inactive";
      }
    },
    classes() {
      const array = [];

      if (this.model.meta?.deleted) {
        array.push("deleted");
      }

      if (this.model.meta?.status === "inactive") {
        array.push("inactive");
      }

      return array;
    },
    hasIcon() {
      switch (this.basicType) {
        case "formrequest":
          return true;
          break;
      }
    },
    iconClass() {
      switch (this.basicType) {
        case "formrequest":
          return this.model.complete ? "complete" : "pending";
          break;
      }
    },
    icon() {
      switch (this.basicType) {
        case "formrequest":
          return this.model.complete ? "fa-check-circle" : "fa-clock";
          break;
      }
    },
    summary() {
      switch (this.basicType) {
        case "facility":
          if (this.model.venue?.title) {
            return this.model.venue.title;
          }
          break;
        case "event":
          return [
            this.model.subtitle,
            this.$sdk.date.readableEventDate(this.model),
          ]
            .filter(Boolean)
            .join(" • ");
          break;
        case "formrequest":
          return this.model.complete ? "Form completed" : "Awaiting response";
          break;
      }

      return this.model.subtitle;
    },
    basicType() {
      let type = this.model?.meta?.type;

      if (!type) {
        const _id = this.$sdk.utils.id(this.model);
        var slicedHexKey = (_id || "").slice(8, 10);
        switch (slicedHexKey) {
          case "69":
            type = "image";
            break;
          case "76":
            type = "video";
            break;
          case "70":
            type = "profile";
            break;
          case "65":
            type = "event";
            break;
        }
      }
      return type;
    },
    hasAvatar() {
      switch (this.basicType) {
        case "profile":
        case "persona":
        case "user":
          return true;
      }
    },
    hasImage() {
      switch (this.basicType) {
        case "image":
        case "video":
        case "profile":
          return true;
      }
    },
    title() {
      return (
        this.model.title ||
        this.model.name ||
        this.model.label ||
        (this.model.firstName
          ? `${this.model.firstName} ${this.model.lastName}`
          : "(Title unknown)")
      );
    },
  },
  emits: ["clicked"],
  methods: {
    clicked() {
      if (this.manual) {
        this.$emit("clicked", this.model);
      } else {
        this.$sdk.dispatch("item:view", this.model);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.content-item {
  padding: 0.5em 0.8em;
  // border: 1px solid var(--fg5, rgba(#000, 0.1));
  border-radius: 0.3em;
  // background: var(--bg1, #fff);
  cursor: pointer;
  margin-bottom: 1px;
  position: relative;

  .bar {
    left: 0;
    top: 0;
    bottom: 0;
    position: absolute;
    width: 0.2em;
    display: block;
  }

  .item-summary {
    opacity: 0.5;
    font-size: 0.9em;
  }

  .item-title {
    font-weight: 500;
  }

  @media (prefers-color-scheme: light) {
    background: var(--bg1, #fff);
    border: 1px solid color-mix(in lab, currentColor 15%, transparent);
  }

  @media (prefers-color-scheme: dark) {
    background: color-mix(in lab, #000 1%, transparent);
    border: 1px solid color-mix(in lab, currentColor 18%, transparent);
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.deleted {
    .text {
      color: var(--error, red);
      text-decoration: line-through;
    }
  }

  &.inactive {
    .text {
      opacity: 0.5;
    }
  }

  .text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: pre-line;
  }

  .icon-wrapper {
    width: 25px;
    font-size: 20px;
    text-align: center;

    &.complete {
      color: var(--success, green);
    }

    &.pending {
      color: var(--warning, orange);
    }
  }
  .image-wrapper {
    width: 2em;
    max-width: 50px;

    &.profile {
      width: 2em;
      height: 2em;
      margin-right: 0.2em;
      background: rgba(#000, 0.1);
      border-radius: 100%;
    }
  }

  &:hover {
    cursor: pointer;
    background: color-mix(in lab, currentColor 1.5%, var(--bg1));
  }

  .pill {
    border-radius: 3em;
    padding: 0.3em 0.6em;
    display: block;
    text-transform: uppercase;
    font-weight: 500;
    letter-spacing: 0.05em;
    font-size: 0.7em;
    background: color-mix(in lab, currentColor 10%, transparent);
    color: color-mix(in lab, currentColor, transparent);
    border: 1px solid;

    &.live,
    &.success,
    &.paid,
    &.active {
      background: #e0f3bf;
      color: #569e2f;

      @media (prefers-color-scheme: dark) {
        background: none;
      }
    }

    &.sandbox,
    &.pending,
    &.cancelling {
      background: #fbecbb;
      color: #d17a0e;

      @media (prefers-color-scheme: dark) {
        background: none;
      }
    }

    &.error,
    &.unpaid,
    &.deleted,
    &.failed {
      background: #ffdddd;
      color: #b14e4e;

      @media (prefers-color-scheme: dark) {
        background: none;
      }
    }
  }
}
</style>
